import axios from 'axios';
import { useState } from 'react';
import { Modal } from '../../components/Modal/Modal';
import { axiosInstance } from '../../helpers/axios';
import styles from './VideoDialog.module.scss';

interface VideoDialogProps {
    onInsert: (src: string) => void;
    onClose: () => void;
}

// окно вставки ролика: ссылку проверяет бэкенд и возвращает адрес плеера,
// плеер показывается тут же, и только после его загрузки можно вставить
export const VideoDialog = ({ onInsert, onClose }: VideoDialogProps) => {
    const [link, setLink] = useState<string>('');
    const [player, setPlayer] = useState<string>('');
    const [isReady, setIsReady] = useState<boolean>(false);
    const [isChecking, setIsChecking] = useState<boolean>(false);
    const [isAutoplay, setIsAutoplay] = useState<boolean>(false);
    const [error, setError] = useState<string>('');

    const checkLink = async (autoplay: boolean) => {
        if (!link.trim()) {
            return;
        }
        setIsChecking(true);
        setError('');
        setPlayer('');
        setIsReady(false);
        try {
            const { data } = await axiosInstance.post<{ url: string }>(
                '/v1/article/video/',
                { url: link, autoplay },
            );
            setPlayer(data.url);
        } catch (requestError) {
            const detail = axios.isAxiosError(requestError)
                ? requestError.response?.data?.url
                : undefined;
            setError(
                Array.isArray(detail)
                    ? String(detail[0])
                    : 'Не получилось открыть ролик по этой ссылке',
            );
        } finally {
            setIsChecking(false);
        }
    };

    return (
        <Modal>
            <div className={styles.dialog}>
                <h3 className={styles.dialog__header}>
                    Вставьте ссылку на видео
                </h3>
                <form
                    className={styles.dialog__form}
                    onSubmit={(event) => {
                        event.preventDefault();
                        checkLink(isAutoplay);
                    }}
                >
                    <input
                        className={styles.dialog__field}
                        value={link}
                        autoFocus
                        onChange={(event) => setLink(event.target.value)}
                    />
                    <button
                        className={styles.dialog__check}
                        type="submit"
                        disabled={!link.trim() || isChecking}
                    >
                        {isChecking ? 'Проверяем…' : 'Показать'}
                    </button>
                </form>
                <p className={styles.dialog__note}>
                    Ролик VK Видео или RuTube. Можно вставить и код плеера
                    целиком: настройки из него сохранятся.
                </p>
                <label className={styles.dialog__autoplay}>
                    <input
                        type="checkbox"
                        checked={isAutoplay}
                        onChange={(event) => {
                            setIsAutoplay(event.target.checked);
                            if (player) {
                                checkLink(event.target.checked);
                            }
                        }}
                    />
                    Автовоспроизведение
                    <span className={styles.dialog__autoplay_note}>
                        браузеры запускают такой ролик без звука
                    </span>
                </label>
                {error ? (
                    <p className={styles.dialog__error} aria-live="polite">
                        {error}
                    </p>
                ) : null}
                {player ? (
                    <div className={styles.dialog__player}>
                        <iframe
                            src={player}
                            title="Предпросмотр ролика"
                            allow="autoplay; encrypted-media; fullscreen; picture-in-picture"
                            allowFullScreen
                            frameBorder="0"
                            onLoad={() => setIsReady(true)}
                        />
                    </div>
                ) : null}
                <div className={styles.dialog__buttons}>
                    {/* «Вставить» появляется, только когда плеер загрузился */}
                    {isReady ? (
                        <button
                            className={styles.dialog__insert}
                            type="button"
                            onClick={() => onInsert(player)}
                        >
                            Вставить
                        </button>
                    ) : null}
                    <button
                        className={styles.dialog__cancel}
                        type="button"
                        onClick={onClose}
                    >
                        Отмена
                    </button>
                </div>
            </div>
        </Modal>
    );
};
